import { DownloadCSVStringValues, GateTypes, PASSCODE_LIST_TYPE, PasscodeList } from '@/types/registration.types';
import Button from '@ui/buttons/button/button';
import Container from '@ui/container/container';
import Icon from '@ui/icons/icon';
import Text from '@ui/text/text/text';
import { ICONS } from '@ui/icons/types';

import styles from './add-new-passcode-list-card.module.css';
import classNames from 'classnames';
import Popover from '@ui/popover/popover';
import { useAppDispatch, useAppSelector } from '@/store/hooks';
import { selectWorkingProjectRegistrationSettings } from '@/store/slices/project/selectors';
import { saveWorkingProjectByFields } from '@/store/slices/project';
import toastTrigger from '@ui/toast/toast-trigger';
import { downloadCsv, getExportDate } from '../../utils';
import { HandleDownloadCSV } from '@/connection/audiences/passcode-lists';
import { NavLink, useNavigate, useParams } from 'react-router-dom';

type PasscodeListCardProps = {
  passcodeList: PasscodeList;
};

const PasscodeListDisplayProps: Record<number, PasscodeListDisplayParams> = {
  [PASSCODE_LIST_TYPE.EMAILS_ONLY]: { title: 'Emails', gradientClass: 'gradient-blue-r-l-light' },
  [PASSCODE_LIST_TYPE.PASSCODES_ONLY]: { title: 'Passcodes', gradientClass: 'gradient-yellow-r-l-light' },
  [PASSCODE_LIST_TYPE.EMAILS_AND_PASSCODES]: { title: 'Email & Passcode', gradientClass: 'gradient-pink-r-l-light' },
  [PASSCODE_LIST_TYPE.MAGIC_LINKS]: { title: 'Magic Links', gradientClass: 'gradient-red-r-l-light' },
};

type PasscodeListDisplayParams = {
  title: string;
  gradientClass: string;
};

const PasscodeListCard = ({ passcodeList }: PasscodeListCardProps) => {
  const dispatch = useAppDispatch();
  const navigate = useNavigate();
  const { projectUuid, language } = useParams();

  const registrationSettings = useAppSelector(selectWorkingProjectRegistrationSettings);

  const handleRemoveListFromProject = (passcodeListId?: number) => {
    if (!passcodeListId || !registrationSettings) return;

    const currentPasscodeRequirements = registrationSettings.requirements[GateTypes.passcode_list] || [];
    const newPasscodeRequirements = currentPasscodeRequirements.filter(
      (requirement) => !requirement.passcodeList?.includes(passcodeListId)
    );

    const _registrationSettings = {
      ...registrationSettings,
      requirements: {
        ...registrationSettings.requirements,
        [GateTypes.passcode_list]: newPasscodeRequirements,
      },
    };

    dispatch(saveWorkingProjectByFields({ registration_settings: _registrationSettings }));
  };

  const handleDownloadPasscodeList = async () => {
    try {
      const response = await HandleDownloadCSV(
        passcodeList,
        DownloadCSVStringValues.NORMAL,
        undefined,
        null,
        passcodeList.dma_id
      );

      if (!response.ok) {
        toastTrigger(
          <div className="S1">
            <div>There was an error downloading the magic links.</div>
          </div>,
          {
            type: 'error',
            autoClose: 10000,
          }
        );
        return;
      }

      downloadCsv(response, `${passcodeList.name}-${getExportDate()}`);
    } catch (e) {
      console.error('Error downloading magic links:', e);
      toastTrigger(
        <div className="S1">
          <div>Download failed</div>
          <div>Your audience list failed to generate</div>
        </div>,
        {
          type: 'error',
          autoClose: 10000,
        }
      );
    }
  };

  const handleCardClick = () => {
    navigate(`/project/edit/${projectUuid}/${language}/audiences/list/${passcodeList.passcode_list}`);
  };

  return (
    <Container
      height="180px"
      width="320px"
      className={classNames(
        'padding-h-20 padding-b-20 flex fd-column jc-space-between radius-16 cursor-pointer',
        styles[PasscodeListDisplayProps[passcodeList.type]?.gradientClass]
      )}
      onClick={handleCardClick} // keyboard accessibility not necessary here as the Popover below will catch tabs
    >
      <Container className="flex fd-row jc-space-between" height="125px" background="transparent">
        <Text tag="h5" clamp={5} className="H5 padding-t-20">
          {passcodeList.name}
        </Text>
        <Container onClick={(e) => e.stopPropagation()}>
          <Popover
            width="125px"
            buttonBgColor="transparent"
            containerClassName="flex fd-row jc-flex-start margin-t-14"
            contentClassName="margin-t-4"
            overflow="visible"
            icon={ICONS.THREE_DOTS_VERTICAL}
            iconColor="var(--gray-100)"
            iconColorLightMode="var(--gray-100)"
            cycleWithArrowKeys
            closeOnClick
          >
            <NavLink
              className={styles['passcode-list-edit']}
              style={{ padding: '.6em 1.2em' }}
              to={`/project/edit/${projectUuid}/${language}/audiences/list/${passcodeList.passcode_list}`}
            >
              View list
            </NavLink>

            <Button onClick={handleDownloadPasscodeList}>Download</Button>
            <Button onClick={() => handleRemoveListFromProject(passcodeList.passcode_list)}>
              <Text color="red">Remove</Text>
            </Button>
          </Popover>
        </Container>
      </Container>

      <Container height="32px" background="transparent" className="flex fd-row jc-space-between ai-flex-end margin-t-8">
        <Container
          background="rgba(var(--primitives-mode-1-gray-1200-rgb), 0.2)"
          padding="8px 10px"
          className="flex fd-row jc-flex-end radius-8"
          style={{ lineHeight: '13px' }}
        >
          <Text style={{ fontSize: '11px', lineHeight: '16px' }}>
            {PasscodeListDisplayProps[passcodeList.type]?.title || 'no match'}
          </Text>
        </Container>

        <Container
          background="rgba(var(--primitives-mode-1-gray-1200-rgb), 0.2)"
          padding="8px 10px"
          className="flex fd-row jc-flex-end radius-8"
          style={{ lineHeight: '13px' }}
        >
          <Icon name={ICONS.PEOPLE_NAV} size={13} color="var(--gray-100)" />
          <Text fontSize={'13px'} className="margin-l-4">
            {passcodeList.member_count}
          </Text>
        </Container>
      </Container>
    </Container>
  );
};

export default PasscodeListCard;
